// src/pages/SignupPage.js
//
// Ticket 14: self-serve org signup.
//
// Form fields: email, display name, optional organization name,
// password, confirm password.
//
// On submit we:
//
//   1. createUserWithEmailAndPassword — Firebase Auth account.
//   2. updateProfile displayName.
//   3. ensureUserDoc with globalRole='user', legacyPasswordMigrated=true.
//   4. If the email has no pending invites, provision a default org:
//      createOrganization in `pending` status, then create the
//      orgAdmin membership and the default study.
//   5. If the email was invited instead, skip org creation and let the
//      invite flow take over after auth.
//
// The provisioning helper is resumable so `auth/email-already-in-use`
// can recover a partial earlier signup instead of leaving an orphaned
// user with no org and nothing to approve.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { Eye, EyeOff } from 'lucide-react';
import { auth } from '../firebase';
import AuthDataNotice from '../components/AuthDataNotice';
import { ensureUserDoc } from '../auth/userHelpers';
import { ensureSignupAccess } from '../auth/signupProvisioning';

const MIN_PASSWORD_LENGTH = 8;

// Design-system tokens pulled from src/design-system/DesignSystem.jsx
// (lightTokens). Applied inline since the rest of the app uses
// Tailwind's default palette and we don't want to bolt a theme config
// onto a single surface.
const BRAND = {
  accent: '#2C5F8A',
  accentDeep: '#1A4060',
  accentLight: '#E8F0F8',
  bg: '#F6F5F3',
  card: '#FFFFFF',
  sunken: '#F0EFEC',
  border: '#E2E0DA',
  borderStrong: '#C8C5BC',
  ink: '#1A1A1A',
  inkSec: '#5C5C5C',
  inkTer: '#8A8A8A',
  serif: "'Newsreader', Georgia, serif",
};

const SignupPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = () => {
    if (!email.trim()) return 'Email is required.';
    if (!displayName.trim()) return 'Display name is required.';
    if (password.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (password !== confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setLoadingText('Creating your account...');
    const normalizedEmail = email.trim().toLowerCase();
    const trimmedName = displayName.trim();
    const trimmedOrgName = orgName.trim();

    const finishSignup = async (credential, { isNewUser }) => {
      const uid = credential.user.uid;

      if (trimmedName && credential.user.displayName !== trimmedName) {
        await updateProfile(credential.user, { displayName: trimmedName });
      }

      setLoadingText('Setting up your profile...');
      await ensureUserDoc(uid, {
        email: normalizedEmail,
        overrides: {
          displayName: trimmedName,
          legacyPasswordMigrated: true,
          ...(isNewUser ? { globalRole: 'user' } : {}),
        },
      });

      setLoadingText('Setting up your access...');
      const result = await ensureSignupAccess({
        uid,
        email: normalizedEmail,
        displayName: trimmedName,
        requestedOrgName: trimmedOrgName,
      });

      if (result.kind === 'pending-invite') {
        setLoadingText('Your invite is ready...');
      } else if (result.kind === 'existing-membership') {
        setLoadingText('Done — signing you in...');
      } else {
        setLoadingText('Done — waiting for approval...');
      }

      navigate('/');
    };

    try {
      const credential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password,
      );
      await finishSignup(credential, { isNewUser: true });
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        try {
          setLoadingText('Resuming your existing account...');
          const credential = await signInWithEmailAndPassword(
            auth,
            normalizedEmail,
            password,
          );
          await finishSignup(credential, { isNewUser: false });
          return;
        } catch (resumeErr) {
          if (
            resumeErr.code === 'auth/wrong-password'
            || resumeErr.code === 'auth/invalid-credential'
          ) {
            setError(
              'An account with this email already exists. Sign in instead, or reset the password if this is your account.',
            );
          } else {
            setError(`Could not resume your existing account: ${resumeErr.message}`);
          }
        }
      } else if (err.code === 'auth/weak-password') {
        setError(`Password is too weak — use at least ${MIN_PASSWORD_LENGTH} characters.`);
      } else if (err.code === 'auth/invalid-email') {
        setError('That email address does not look valid.');
      } else {
        setError(`Could not create your account: ${err.message}`);
      }
      setLoading(false);
    }
  };

  const inputBaseStyle = {
    width: '100%',
    padding: '11px 14px',
    background: BRAND.card,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 8,
    color: BRAND.ink,
    fontSize: 14,
    lineHeight: 1.4,
    outline: 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
  };
  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: BRAND.inkSec,
    marginBottom: 8,
  };
  const onInputFocus = (e) => {
    e.currentTarget.style.borderColor = BRAND.accent;
    e.currentTarget.style.boxShadow = `0 0 0 3px ${BRAND.accentLight}`;
  };
  const onInputBlur = (e) => {
    e.currentTarget.style.borderColor = BRAND.border;
    e.currentTarget.style.boxShadow = 'none';
  };

  const renderPasswordField = ({
    id,
    label,
    value,
    setValue,
    shown,
    setShown,
    autoComplete,
    placeholder,
    minLength,
  }) => (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          autoComplete={autoComplete}
          minLength={minLength}
          placeholder={placeholder}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          style={{ ...inputBaseStyle, paddingRight: 44 }}
        />
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            padding: 6,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: shown ? BRAND.accent : BRAND.inkTer,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {shown ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `radial-gradient(ellipse at top, ${BRAND.accentLight} 0%, ${BRAND.bg} 55%)`,
      }}
    >
      <div
        className="w-full max-w-md"
        style={{
          background: BRAND.card,
          borderRadius: 16,
          border: `1px solid ${BRAND.border}`,
          boxShadow:
            '0 1px 2px rgba(26, 64, 96, 0.04), 0 12px 32px rgba(26, 64, 96, 0.10)',
          overflow: 'hidden',
        }}
      >
        {/* Accent rail — one thin line in brand accent, a small
            premium touch that anchors the card to the palette. */}
        <div
          style={{
            height: 3,
            background: `linear-gradient(90deg, ${BRAND.accent}, ${BRAND.accentDeep})`,
          }}
        />

        <div className="p-8 sm:p-10">
          <div className="text-center mb-8">
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: BRAND.accentLight,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                marginBottom: 20,
                border: `1px solid ${BRAND.border}`,
              }}
            >
              📚
            </div>
            <h1
              style={{
                fontFamily: BRAND.serif,
                fontSize: 28,
                fontWeight: 500,
                letterSpacing: '-0.02em',
                color: BRAND.ink,
                margin: 0,
              }}
            >
              Create your account
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="su-email" style={labelStyle}>
                Email address
              </label>
              <input
                id="su-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@institution.edu"
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                style={inputBaseStyle}
              />
            </div>

            <div>
              <label htmlFor="su-name" style={labelStyle}>
                Your display name
              </label>
              <input
                id="su-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                autoComplete="name"
                placeholder="Dr. Amara Osei"
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                style={inputBaseStyle}
              />
            </div>

            <div>
              <label htmlFor="su-org" style={labelStyle}>
                Organization name (optional)
              </label>
              <input
                id="su-org"
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Leave blank to use a default organization"
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                style={inputBaseStyle}
              />
              <p
                style={{
                  fontSize: 12,
                  color: BRAND.inkSec,
                  marginTop: 8,
                  marginBottom: 0,
                }}
              >
                If this email was invited to an existing organization, we will use
                that invite instead of creating a new one.
              </p>
            </div>

            {renderPasswordField({
              id: 'su-pass',
              label: 'Password',
              value: password,
              setValue: setPassword,
              shown: showPassword,
              setShown: setShowPassword,
              autoComplete: 'new-password',
              placeholder: 'At least 8 characters',
              minLength: MIN_PASSWORD_LENGTH,
            })}

            {renderPasswordField({
              id: 'su-pass2',
              label: 'Confirm password',
              value: confirmPassword,
              setValue: setConfirmPassword,
              shown: showConfirmPassword,
              setShown: setShowConfirmPassword,
              autoComplete: 'new-password',
              placeholder: 'Repeat your password',
              minLength: MIN_PASSWORD_LENGTH,
            })}

            {error && (
              <div
                style={{
                  background: '#FCE8E8',
                  border: '1px solid #F4C9C9',
                  color: '#8A1E1E',
                  padding: '10px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                  textAlign: 'center',
                }}
              >
                {error}
              </div>
            )}

            <AuthDataNotice />

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: loading
                  ? BRAND.accentDeep
                  : `linear-gradient(180deg, ${BRAND.accent}, ${BRAND.accentDeep})`,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '0.01em',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.75 : 1,
                boxShadow: '0 1px 2px rgba(26, 64, 96, 0.2)',
                transition: 'transform 120ms ease, box-shadow 120ms ease',
              }}
              onMouseDown={(e) => {
                if (!loading) e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {loading ? loadingText || 'Creating account...' : 'Create account'}
            </button>

            <p
              style={{
                textAlign: 'center',
                fontSize: 13,
                color: BRAND.inkSec,
                margin: 0,
                paddingTop: 4,
              }}
            >
              Already have an account?{' '}
              <Link
                to="/login"
                style={{
                  color: BRAND.accent,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
